import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, X, Check, Ban, Trash2, UserPlus, Home, Plus } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import useBulkSelect from "../../lib/useBulkSelect";
import BulkActionBar, { BulkCheckbox } from "../../components/BulkActionBar";
import { VipBadge } from "../../components/VipBadge";
import PostalAddressFields, { EMPTY_ADDRESS } from "../../components/PostalAddressFields";
import CountrySelect from "../../components/CountrySelect";

const APT_TYPES = ["T0", "T1", "T2", "T3", "T4", "T5", "VS", "VM", "VL", "HOTEL"];

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // user object
  const [creating, setCreating] = useState(false);
  const [aptsOf, setAptsOf] = useState(null);   // client object whose apartments we're managing

  const load = () => api.get("/admin/users").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  async function approve(id) {
    try { await api.post(`/admin/users/${id}/approve`); toast.success("Aprovado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function revoke(id) {
    try { await api.post(`/admin/users/${id}/revoke`); toast.success("Revogado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function remove(id) {
    if (!window.confirm("Eliminar este utilizador?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function saveEdit() {
    try {
      const payload = {
        name: editing.name,
        email: editing.email,
        username: editing.username || "",
        phone: editing.phone || "",
        // Dados fiscais para faturação Facturalusa.
        nif: editing.nif || "",
        address: editing.address || "",
        city: editing.city || "",
        postal_code: editing.postal_code || "",
        country: editing.country || "Portugal",
      };
      if (editing.new_password) payload.password = editing.new_password;
      await api.put(`/admin/users/${editing.id}`, payload);
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const filtered = items.filter(u => filter === "all" ? true : u.role === filter);
  const pendingCount = items.filter(u => u.role === "employee" && !u.approved).length;

  // Bulk select — admins can't be bulk-edited (protected)
  const selectableIds = useMemo(() => filtered.filter(u => u.role !== "admin").map(u => u.id), [filtered]);
  const bulk = useBulkSelect(selectableIds);
  async function bulkRun(ids, fn, { successMsg }) {
    const res = await Promise.allSettled(ids.map(fn));
    const ok = res.filter(r => r.status === "fulfilled").length;
    const ko = res.length - ok;
    if (ok > 0) toast.success(`${ok} ${successMsg}${ko > 0 ? ` · ${ko} falharam` : ""}`);
    else toast.error("Nenhuma operação concluída");
    load();
  }

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-3">Gestão</div>
          <h1 className="font-display text-5xl">Utilizadores</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {pendingCount > 0 && (
            <button
              onClick={() => setFilter("employee")}
              className="px-4 py-2 text-xs uppercase tracking-widest border border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/5 rounded-sm animate-pulse"
              data-testid="pending-approval-badge">
              {pendingCount} funcionário{pendingCount > 1 ? "s" : ""} a aguardar aprovação
            </button>
          )}
          <button
            onClick={() => setCreating(true)}
            className="btn-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
            data-testid="new-user-btn">
            <UserPlus size={14} /> Novo cliente
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { id: "all", label: "Todos" },
          { id: "client", label: "Clientes" },
          { id: "employee", label: "Funcionários" },
          { id: "admin", label: "Admins" },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm ${filter === f.id ? "border-[#d4af37] text-[#d4af37] bg-[#062c27]" : "border-[#1a4640] text-[#a0b2af]"}`}
                  data-testid={`filter-${f.id}`}>{f.label}</button>
        ))}
      </div>

      {/* MOBILE: cards stacked vertically with clearly visible action buttons */}
      <div className="md:hidden space-y-3">
        {filtered.length === 0 && <div className="lux-card p-6 text-center text-sm text-[#a0b2af]">Sem resultados.</div>}
        {filtered.map(u => (
          <div key={u.id} className="lux-card p-5" data-testid={`user-card-${u.id}`}>
            <div className="flex items-start gap-3">
              {u.role !== "admin" && (
                <div className="pt-1">
                  <BulkCheckbox
                    id={u.id}
                    checked={bulk.isSelected(u.id)}
                    onChange={() => bulk.toggle(u.id)}
                    label="Selecionar utilizador"
                  />
                </div>
              )}
              <div className="flex items-start justify-between gap-3 flex-1 min-w-0">
              <div className="flex-1 min-w-0">
                {u.registration_number && (
                  <div className="font-mono text-[10px] text-[#d4af37] mb-1">{u.registration_number}</div>
                )}
                <div className="font-display text-xl">{u.name}</div>
                {u.role === "client" && u.is_vip && (
                  <div className="mt-1"><VipBadge size="sm" /></div>
                )}
                <div className="text-xs text-[#a0b2af] mt-1 break-all">{u.email}</div>
                {u.username && <div className="text-[11px] text-[#a0b2af]">@{u.username}</div>}
                {u.phone && <div className="text-[11px] text-[#d4af37] mt-1">📞 {u.phone}</div>}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#1a4640] rounded-sm">{u.role}</span>
                {u.role === "employee" && (
                  <span className={`text-[10px] uppercase tracking-widest ${u.approved ? "text-[#10b981]" : "text-[#f59e0b]"}`}>
                    {u.approved ? "Aprovado" : "Pendente"}
                  </span>
                )}
              </div>
            </div>
            </div>

            {/* ACTION BUTTONS — always vertically stacked and prominent on mobile */}
            <div className="mt-4 pt-4 border-t border-[#1a4640] grid grid-cols-2 gap-2">
              {u.role === "employee" && !u.approved && (
                <button onClick={() => approve(u.id)}
                        className="col-span-2 btn-gold px-3 py-2.5 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
                        data-testid={`approve-${u.id}`}>
                  <Check size={14} /> Aprovar funcionário
                </button>
              )}
              {u.role === "employee" && u.approved && (
                <button onClick={() => revoke(u.id)}
                        className="col-span-2 text-xs uppercase tracking-widest border border-[#ef4444]/40 text-[#fca5a5] px-3 py-2.5 inline-flex items-center justify-center gap-2 hover:bg-[#ef4444]/10"
                        data-testid={`revoke-${u.id}`}>
                  <Ban size={14} /> Revogar acesso
                </button>
              )}
              {u.role !== "admin" && (
                <>
                  {u.role === "client" && (
                    <button onClick={() => setAptsOf(u)}
                            className="btn-ghost-gold px-3 py-2 text-xs inline-flex items-center justify-center gap-1"
                            data-testid={`apts-${u.id}`}>
                      <Home size={12} /> Apartamentos
                    </button>
                  )}
                  <button onClick={() => setEditing({ ...u, new_password: "" })}
                          className="btn-ghost-gold px-3 py-2 text-xs inline-flex items-center justify-center gap-1"
                          data-testid={`edit-${u.id}`}>
                    <Pencil size={12} /> Editar
                  </button>
                  <button onClick={() => remove(u.id)}
                          className="text-xs text-[#fca5a5] border border-[#ef4444]/30 px-3 py-2 inline-flex items-center justify-center gap-1"
                          data-testid={`delete-${u.id}`}>
                    <Trash2 size={12} /> Eliminar
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* DESKTOP: table */}
      <div className="hidden md:block lux-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#0a3a33] text-[#a0b2af] text-xs uppercase tracking-widest">
            <tr>
              <th className="p-4 w-10">
                <BulkCheckbox
                  checked={bulk.allSelected}
                  indeterminate={bulk.someSelected}
                  onChange={() => bulk.toggleAll(selectableIds)}
                  label="Selecionar todos"
                />
              </th>
              <th className="text-left p-4">Nº Reg.</th>
              <th className="text-left p-4">Nome</th>
              <th className="text-left p-4">Email / User</th>
              <th className="text-left p-4">Papel</th>
              <th className="text-left p-4">Estado</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#a0b2af]">Sem resultados.</td></tr>}
            {filtered.map(u => (
              <tr key={u.id} className="border-t border-[#1a4640]" data-testid={`user-${u.id}`}>
                <td className="p-4">
                  {u.role !== "admin" && (
                    <BulkCheckbox
                      id={u.id}
                      checked={bulk.isSelected(u.id)}
                      onChange={() => bulk.toggle(u.id)}
                      label="Selecionar"
                    />
                  )}
                </td>
                <td className="p-4 font-mono text-xs text-[#d4af37]">{u.registration_number || "—"}</td>
                <td className="p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{u.name}</span>
                    {u.role === "client" && u.is_vip && <VipBadge size="sm" />}
                  </div>
                </td>
                <td className="p-4 text-[#a0b2af]">
                  <div>{u.email}</div>
                  {u.username && <div className="text-xs">@{u.username}</div>}
                </td>
                <td className="p-4"><span className="text-xs uppercase tracking-widest">{u.role}</span></td>
                <td className="p-4">
                  {u.role === "employee" ? (
                    <span className={`text-xs ${u.approved ? "text-[#10b981]" : "text-[#f59e0b]"}`}>
                      {u.approved ? "Aprovado" : "Pendente"}
                    </span>
                  ) : <span className="text-xs text-[#a0b2af]">—</span>}
                </td>
                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                  {u.role === "client" && (
                    <button onClick={() => setAptsOf(u)}
                            className="btn-ghost-gold px-3 py-1 text-xs inline-flex items-center gap-1"
                            data-testid={`apts-desktop-${u.id}`}>
                      <Home size={12} /> Apartamentos
                    </button>
                  )}
                  {u.role !== "admin" && (
                    <button onClick={() => setEditing({ ...u, new_password: "" })}
                            className="btn-ghost-gold px-3 py-1 text-xs inline-flex items-center gap-1"
                            data-testid={`edit-desktop-${u.id}`}>
                      <Pencil size={12} /> Editar
                    </button>
                  )}
                  {u.role === "employee" && !u.approved && (
                    <button onClick={() => approve(u.id)} className="btn-gold px-3 py-1 text-xs inline-flex items-center gap-1" data-testid={`approve-desktop-${u.id}`}>
                      <Check size={12} /> Aprovar
                    </button>
                  )}
                  {u.role === "employee" && u.approved && (
                    <button onClick={() => revoke(u.id)} className="text-xs text-[#fca5a5] border border-[#ef4444]/40 px-3 py-1" data-testid={`revoke-desktop-${u.id}`}>Revogar</button>
                  )}
                  {u.role !== "admin" && (
                    <button onClick={() => remove(u.id)} className="text-xs text-[#fca5a5] px-3 py-1" data-testid={`delete-desktop-${u.id}`}>Eliminar</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-[#062c27] border border-[#1a4640] max-w-2xl w-full p-6 space-y-4 my-8" data-testid="user-editor">
            <div className="flex items-center justify-between">
              <div>
                <div className="eyebrow">Editar utilizador</div>
                <div className="font-display text-2xl">{editing.name}</div>
                {editing.registration_number && <div className="text-xs text-[#d4af37] mt-1 font-mono">{editing.registration_number}</div>}
              </div>
              <button onClick={() => setEditing(null)} className="text-[#a0b2af] hover:text-white"><X size={18} /></button>
            </div>

            {/* Bloco — identificação */}
            <div className="grid sm:grid-cols-2 gap-3">
              <EditField label="Nome" value={editing.name} onChange={v => setEditing(ed => ({ ...ed, name: v }))} testId="edit-name" />
              <EditField label="Email" value={editing.email} onChange={v => setEditing(ed => ({ ...ed, email: v }))} testId="edit-email" />
              <EditField label="Utilizador" value={editing.username || ""} onChange={v => setEditing(ed => ({ ...ed, username: v }))} testId="edit-username" />
              <EditField label="Telefone" value={editing.phone || ""} onChange={v => setEditing(ed => ({ ...ed, phone: v }))} testId="edit-phone" />
            </div>

            {/* Bloco fiscal — apenas para clientes (afeta faturas Facturalusa) */}
            {editing.role === "client" && (
              <div className="pt-2 mt-2 border-t border-[#1a4640]">
                <div className="eyebrow text-[#d4af37] mb-3">Dados de faturação · Facturalusa</div>
                <p className="text-xs text-[#a0b2af] mb-3 leading-relaxed">
                  Estes dados são enviados para a Facturalusa quando emitimos
                  uma fatura. Se o NIF for omitido, será usado o NIF genérico
                  de consumidor final (999999990).
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <EditField
                    label="NIF / Contribuinte"
                    value={editing.nif || ""}
                    onChange={v => setEditing(ed => ({ ...ed, nif: v }))}
                    testId="edit-nif"
                  />
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-[#a0b2af]">País</label>
                    <CountrySelect value={editing.country || "Portugal"} onChange={v => setEditing(ed => ({ ...ed, country: v }))}
                                   testId="edit-country" className="w-full bg-[#041f1b] border border-[#1a4640] rounded-sm px-4 py-3 text-sm outline-none focus:border-[#d4af37]" />
                  </div>
                  <div className="sm:col-span-2">
                    <EditField
                      label="Morada (rua, nº)"
                      value={editing.address || ""}
                      onChange={v => setEditing(ed => ({ ...ed, address: v }))}
                      testId="edit-address"
                    />
                  </div>
                  <EditField
                    label="Localidade"
                    value={editing.city || ""}
                    onChange={v => setEditing(ed => ({ ...ed, city: v }))}
                    testId="edit-city"
                  />
                  <EditField
                    label="Código postal"
                    value={editing.postal_code || ""}
                    onChange={v => setEditing(ed => ({ ...ed, postal_code: v }))}
                    testId="edit-postal-code"
                  />
                </div>
              </div>
            )}

            {/* Bloco palavra-passe */}
            <div className="pt-2 mt-2 border-t border-[#1a4640]">
              <EditField label="Nova palavra-passe (deixe vazio para manter)" value={editing.new_password} onChange={v => setEditing(ed => ({ ...ed, new_password: v }))} type="password" testId="edit-password" />
            </div>

            <div className="flex gap-2 pt-4">
              <button onClick={saveEdit} className="btn-gold px-6 py-2 text-sm" data-testid="save-user">Guardar</button>
              <button onClick={() => setEditing(null)} className="px-6 py-2 text-sm text-[#a0b2af]">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onCreated={(u) => {
            setCreating(false);
            load();
            if (u._generated_password) {
              toast.success(`Cliente criado. Password temporária: ${u._generated_password}`, {
                duration: 12000,
              });
            } else {
              toast.success("Cliente criado");
            }
          }}
        />
      )}

      {aptsOf && (
        <ClientApartmentsModal
          client={aptsOf}
          onClose={() => setAptsOf(null)}
        />
      )}

      <BulkActionBar
        count={bulk.count}
        ids={Array.from(bulk.selected)}
        onClear={bulk.clear}
        actions={[
          {
            key: "approve", label: "Aprovar", icon: Check, variant: "gold",
            confirm: "Aprovar {count} utilizador(es)? (aplica-se só a funcionários pendentes)",
            onClick: (ids) => bulkRun(ids, (id) => api.post(`/admin/users/${id}/approve`), { successMsg: "aprovado(s)" }),
          },
          {
            key: "revoke", label: "Revogar", icon: Ban, variant: "default",
            confirm: "Revogar acesso a {count} funcionário(s)?",
            onClick: (ids) => bulkRun(ids, (id) => api.post(`/admin/users/${id}/revoke`), { successMsg: "revogado(s)" }),
          },
          {
            key: "delete", label: "Eliminar", icon: Trash2, variant: "danger",
            confirm: "Eliminar {count} utilizador(es)? Ação irreversível.",
            onClick: (ids) => bulkRun(ids, (id) => api.delete(`/admin/users/${id}`), { successMsg: "eliminado(s)" }),
          },
        ]}
      />
    </div>
  );
}

function EditField({ label, value, onChange, type = "text", testId }) {
  return (
    <div className="space-y-2">
      <label className="text-xs uppercase tracking-widest text-[#a0b2af]">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
             className="w-full bg-[#041f1b] border border-[#1a4640] rounded-sm px-4 py-3 text-sm outline-none focus:border-[#d4af37]"
             data-testid={testId} />
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    role: "client", name: "", email: "", phone: "", username: "",
    nif: "", address: "", city: "", postal_code: "", country: "Portugal",
    password: "",
  });
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit() {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Preencha nome e email."); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", form);
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="bg-[#062c27] border border-[#1a4640] max-w-xl w-full p-6 space-y-4 my-8"
           data-testid="create-user-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow">Nova conta</div>
            <div className="font-display text-2xl">Criar utilizador</div>
            <div className="text-xs text-[#a0b2af] mt-1">
              Clientes ficam ativos imediatamente. Funcionários precisam de aprovação.
            </div>
          </div>
          <button onClick={onClose} className="text-[#a0b2af] hover:text-white"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {[
            { id: "client",   label: "Cliente" },
            { id: "employee", label: "Funcionário" },
          ].map(r => (
            <button key={r.id} type="button" onClick={() => set("role", r.id)}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm ${form.role === r.id ? "border-[#d4af37] text-[#d4af37] bg-[#0a3a33]" : "border-[#1a4640] text-[#a0b2af]"}`}
                    data-testid={`new-user-role-${r.id}`}>
              {r.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <EditField label="Nome *"      value={form.name}     onChange={v => set("name", v)}     testId="new-name" />
          <EditField label="Email *"     value={form.email}    onChange={v => set("email", v)}    testId="new-email" />
          <EditField label="Telemóvel"   value={form.phone}    onChange={v => set("phone", v)}    testId="new-phone" />
          <EditField label="Utilizador"  value={form.username} onChange={v => set("username", v)} testId="new-username" />
          <EditField label="NIF"         value={form.nif}      onChange={v => set("nif", v)}      testId="new-nif" />
          <EditField label="Cidade"      value={form.city}     onChange={v => set("city", v)}     testId="new-city" />
          <div className="sm:col-span-2">
            <EditField label="Morada"    value={form.address}  onChange={v => set("address", v)}  testId="new-address" />
          </div>
          <EditField label="Código Postal" value={form.postal_code} onChange={v => set("postal_code", v)} testId="new-postal" />
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-[#a0b2af]">País</label>
            <CountrySelect value={form.country} onChange={v => set("country", v)} testId="new-country" className="w-full bg-[#041f1b] border border-[#1a4640] rounded-sm px-4 py-3 text-sm outline-none focus:border-[#d4af37]" />
          </div>
          <div className="sm:col-span-2">
            <EditField label="Password (deixe vazio para gerar automaticamente)"
                       type="password" value={form.password}
                       onChange={v => set("password", v)} testId="new-password" />
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <button onClick={submit} disabled={saving}
                  className="btn-gold px-6 py-2 text-sm disabled:opacity-50"
                  data-testid="save-new-user">
            {saving ? "A criar…" : "Criar"}
          </button>
          <button onClick={onClose} className="px-6 py-2 text-sm text-[#a0b2af]">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function ClientApartmentsModal({ client, onClose }) {
  const [apts, setApts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", apt_type: "T1", ...EMPTY_ADDRESS, notes: "" });
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/clients/${client.id}/apartments`);
      setApts(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  async function create() {
    if (!form.name.trim()) {
      toast.error("Indique o nome."); return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/clients/${client.id}/apartments`, form);
      toast.success("Apartamento criado");
      setForm({ name: "", apt_type: "T1", ...EMPTY_ADDRESS, notes: "" });
      setAdding(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function removeApt(aid) {
    if (!window.confirm("Eliminar este apartamento?")) return;
    try {
      await api.delete(`/apartments/${aid}`);
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="bg-[#062c27] border border-[#1a4640] max-w-2xl w-full p-6 space-y-4 my-8"
           data-testid="client-apartments-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow">Apartamentos</div>
            <div className="font-display text-2xl">{client.name}</div>
            <div className="text-xs text-[#a0b2af] mt-1">{client.email}</div>
          </div>
          <button onClick={onClose} className="text-[#a0b2af] hover:text-white"><X size={18} /></button>
        </div>

        <div className="flex items-center justify-between border-t border-[#1a4640] pt-4">
          <div className="text-xs text-[#a0b2af]">
            {loading ? "A carregar…" : `${apts.length} apartamento${apts.length === 1 ? "" : "s"}`}
          </div>
          {!adding && (
            <button onClick={() => setAdding(true)}
                    className="btn-gold px-3 py-1.5 text-xs uppercase tracking-widest inline-flex items-center gap-1"
                    data-testid="add-apartment-btn">
              <Plus size={12} /> Novo apartamento
            </button>
          )}
        </div>

        {adding && (
          <div className="border border-[#d4af37]/40 bg-[#0a3a33] p-4 rounded-sm space-y-3">
            <EditField label="Nome do apartamento *" value={form.name}
                       onChange={v => setForm(f => ({ ...f, name: v }))} testId="apt-name" />
            <div>
              <label className="text-xs uppercase tracking-widest text-[#a0b2af] block mb-2">Tipologia</label>
              <div className="flex flex-wrap gap-2">
                {APT_TYPES.map(t => (
                  <button key={t} type="button" onClick={() => setForm(f => ({ ...f, apt_type: t }))}
                          className={`px-3 py-1.5 text-xs border rounded-sm ${form.apt_type === t ? "border-[#d4af37] text-[#d4af37] bg-[#062c27]" : "border-[#1a4640] text-[#a0b2af]"}`}
                          data-testid={`apt-type-${t}`}>{t}</button>
                ))}
              </div>
            </div>
            <PostalAddressFields value={form} onChange={(v) => setForm(f => ({ ...f, ...v }))} />
            <EditField label="Notas" value={form.notes}
                       onChange={v => setForm(f => ({ ...f, notes: v }))} testId="apt-notes" />
            <div className="flex gap-2">
              <button onClick={create} disabled={saving}
                      className="btn-gold px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50"
                      data-testid="save-apt">
                {saving ? "A guardar…" : "Criar"}
              </button>
              <button onClick={() => { setAdding(false); setForm({ name: "", apt_type: "T1", ...EMPTY_ADDRESS, notes: "" }); }}
                      className="px-4 py-2 text-xs text-[#a0b2af]">Cancelar</button>
            </div>
          </div>
        )}

        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {!loading && apts.length === 0 && !adding && (
            <div className="text-center py-10 text-sm text-[#a0b2af] border border-dashed border-[#1a4640] rounded-sm">
              Este cliente ainda não tem apartamentos registados.
            </div>
          )}
          {apts.map(a => (
            <div key={a.id}
                 className="flex items-start justify-between gap-3 p-3 border border-[#1a4640] rounded-sm bg-[#041f1b]"
                 data-testid={`client-apt-row-${a.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-[#fdfbf7] font-medium">{a.name}</div>
                <div className="text-xs text-[#d4af37] mt-0.5">
                  {a.apt_type} {a.address ? `· ${a.address}` : ""}
                </div>
                {a.notes && <div className="text-[11px] text-[#a0b2af] mt-1">{a.notes}</div>}
              </div>
              <button onClick={() => removeApt(a.id)}
                      className="text-[11px] text-[#fca5a5] border border-[#ef4444]/30 px-3 py-1 rounded-sm shrink-0"
                      data-testid={`del-apt-${a.id}`}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

